import React, { useMemo } from 'react';
import {
  alpha,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TableHeaderCell } from 'components/common/TableHeaderCell';
import { RankingValue } from 'components/common/RankingValue/RankingValue';
import { ScoreCell } from 'components/common/ScoreCell/ScoreCell';

import { CandidatesWithPosition } from '@types';
import { ICandidatesViewState } from '@hooks';
import { CandidateDetailsAction, OrderDirection } from '@constants';
import { ICandidateColumn, resolveCandidateColumns } from '../columns';
import { CandidateLookups, GroupedCandidates } from '../grouping';
import { CandidateGroupHeader } from './CandidateGroupHeader';
import { CandidateListAction } from './CandidateListAction';
import { CandidateMaterials } from './CandidateMaterials';
import { CandidateNameCell } from './CandidateNameCell';
import { MetaCell } from './MetaCell';
import { StackCell } from './StackCell';

interface ICandidatesTableProps {
  groups: GroupedCandidates[];
  lookups: CandidateLookups;
  view: ICandidatesViewState;
  positionNameOf: (candidateId: string) => string | undefined;
  onSort: (key: string) => void;
  onCandidateClick: (id: string) => void;
  onCandidateAction: (id: string, action: CandidateDetailsAction) => void;
}

export const CandidatesTable: React.FC<ICandidatesTableProps> = ({
  groups,
  lookups,
  view,
  positionNameOf,
  onSort,
  onCandidateClick,
  onCandidateAction,
}) => {
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.up('lg'));
  const isWide = useMediaQuery(theme.breakpoints.up('md'));

  const columns = useMemo(
    () =>
      resolveCandidateColumns(
        view.columns,
        isLaptop ? 'lg' : isWide ? 'md' : 'tablet',
      ),
    [view.columns, isLaptop, isWide],
  );

  const { positionTitlesById, workstreamsById } = lookups;

  const hasAnyVideo = groups.some((group) =>
    group.candidates.some((candidate) => Boolean(candidate.videoFileName)),
  );

  const renderCell = (
    column: ICandidateColumn,
    candidate: CandidatesWithPosition,
  ) => {
    switch (column.id) {
      case 'candidate':
        return (
          <CandidateNameCell
            candidate={candidate}
            positionName={isLaptop ? undefined : positionNameOf(candidate.id)}
          />
        );
      case 'workstream':
        return <MetaCell values={workstreamsById.get(candidate.id)} />;
      case 'position':
        return <MetaCell values={positionTitlesById.get(candidate.id)} />;
      case 'tt':
        return <ScoreCell testTask={candidate.testTask} label="test task" />;
      case 'technicalRanking':
        return (
          <RankingValue value={candidate.ranking?.technicalInterview ?? null} />
        );
      case 'softSkills':
        return <RankingValue value={candidate.ranking?.softSkills ?? null} />;
      case 'materials':
        return (
          <CandidateMaterials
            candidate={candidate}
            showVideo={hasAnyVideo}
            onPlay={() => onCandidateClick(candidate.id)}
          />
        );
      case 'stack':
        return <StackCell skills={candidate.skills} />;
      case 'nextSteps':
        return (
          <CandidateListAction
            candidate={candidate}
            onAction={onCandidateAction}
          />
        );
      default:
        return null;
    }
  };

  return (
    // Only a phone scrolls the table sideways. Anywhere else the wrapper must not be a scroll
    // box: `overflow-x: auto` makes it one, and the sticky headers then pin to it instead of
    // the page's own scroll box, so they scroll away with the rows.
    <Box sx={{ overflowX: { xs: 'auto', tablet: 'visible' } }}>
      <Table
        sx={{ tableLayout: 'fixed', width: '100%', backgroundColor: 'white' }}
      >
        <TableHead>
          <TableRow
            sx={{ '& > .MuiTableCell-root': { border: 'none', py: 1 } }}
          >
            {columns.map((column) => (
              <TableHeaderCell
                key={column.id}
                label={column.label}
                sortKey={column.sortKey}
                activeSortKey={view.sort.key}
                sortDir={
                  view.sort.direction === OrderDirection.DESC ? 'desc' : 'asc'
                }
                onSort={column.sortKey ? onSort : undefined}
                width={column.width}
                align={column.align}
                sticky
                stickyOffset={-16}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group, index) => (
            <React.Fragment key={index}>
              {Boolean(group.section) &&
                group.section !== groups[index - 1]?.section && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      sx={(theme) => ({
                        padding: '16px 16px 6px',
                        borderTop: 'none',
                        borderBottom: `1px solid ${theme.palette.background.divider}`,
                      })}
                    >
                      <Typography variant="h3" noWrap>
                        {group.section}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              {group.name && (
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.highlight.neutral,
                    '& > .MuiTableCell-root': { borderBottom: 'none' },
                  }}
                >
                  <TableCell
                    colSpan={columns.length}
                    sx={{
                      padding: '6px 16px',
                      borderTop: 'none',
                      borderBottom: 'none',
                    }}
                  >
                    <CandidateGroupHeader group={group} />
                  </TableCell>
                </TableRow>
              )}
              {group.candidates.map((candidate, candidateIndex) => (
                <TableRow
                  key={candidate.id}
                  onClick={() => onCandidateClick(candidate.id)}
                  sx={(theme) => ({
                    cursor: 'pointer',
                    height: 48,
                    backgroundColor: candidate.isStarred
                      ? theme.palette.background.brandLight
                      : undefined,
                    '& > .MuiTableCell-root': {
                      paddingTop: 0.5,
                      paddingBottom: 0.5,
                      borderBottom:
                        candidateIndex === group.candidates.length - 1 &&
                        index < groups.length - 1
                          ? 'none'
                          : undefined,
                    },
                    '&:hover': {
                      background: candidate.isStarred
                        ? alpha(theme.palette.background.brandLight, 0.7)
                        : theme.palette.highlight.actionable,
                    },
                  })}
                >
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {renderCell(column, candidate)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
